import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest, map, merge, of, throwError, take } from 'rxjs';
import { catchError, distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  ApiResponse,
  AliasSelectOption,
  LOOKUP_TABLES,
  LookupDto,
  LookupSelectOption,
  LookupTableName,
} from '../../interfaces/lookup.interfaces';
import { V2_CALCULATION_PROFILE_OPTIONS } from '../../constants/v2-controlled-value-aliases';

/**
 * Generic LMS lookups service.
 *
 * Callers only pass the lookup table name:
 *   this.lookupService.getOptions('LookupLoanTypeCategories')
 *
 * Labels follow the active UI language the same way i18n files do:
 *   en        → nameEn
 *   ar / sa   → nameAr
 *
 * HTTP responses are cached per table (shareReplay) so list + modal
 * (or multiple subscribers) do not re-hit the same lookup endpoint.
 */
@Injectable({ providedIn: 'root' })
export class LookupService {
  private readonly http = inject(HttpClient);
  private readonly translate = inject(TranslateService);
  private readonly baseUrl = `${environment.baseUrl}/lms/api/lookups`;
  private readonly tableCache = new Map<string, Observable<LookupDto[]>>();

  /** List available lookup table names (if the backend exposes it). */
  getTables(): Observable<string[]> {
    return this.http.get<ApiResponse<string[]> | string[]>(this.baseUrl).pipe(
      map((res) => this.unwrapList<string>(res)),
    );
  }

  /**
   * Fetch raw rows for a lookup table (cached per table name).
   * @example getByTable('LookupLoanTypeCategories')
   * @example getByTable(LOOKUP_TABLES.FinanceFrameworks)
   */
  getByTable(tableName: LookupTableName): Observable<LookupDto[]> {
    const key = String(tableName);
    let cached = this.tableCache.get(key);
    if (!cached) {
      cached = this.http
        .get<ApiResponse<LookupDto[]> | LookupDto[]>(
          `${this.baseUrl}/${encodeURIComponent(key)}`,
        )
        .pipe(
          map((res) => this.unwrapList<LookupDto>(res).map((row) => this.normalize(row))),
          catchError((err) => {
            // Do not poison the cache with a failed request (e.g. early 401).
            this.tableCache.delete(key);
            return throwError(() => err);
          }),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
      this.tableCache.set(key, cached);
    }
    return cached;
  }

  /**
   * Fetch select options for a lookup table, labelled for the current language.
   * Re-emits when the UI language changes (en ↔ ar/sa) without a second HTTP call.
   *
   * Note: this stream does not complete (language changes). Prefer `getOptionsOnce`
   * inside `forkJoin` / one-shot loaders.
   */
  getOptions(tableName: LookupTableName): Observable<LookupSelectOption[]> {
    return combineLatest([this.getByTable(tableName), this.lang$()]).pipe(
      map(([items, lang]) => this.toSelectOptions(items, lang)),
    );
  }

  /**
   * One-shot options for forkJoin / parallel shell loads (completes after first emit).
   */
  getOptionsOnce(tableName: LookupTableName): Observable<LookupSelectOption[]> {
    return this.getOptions(tableName).pipe(take(1));
  }

  /** Convenience wrappers for known tables. */
  /**
   * No longer used by the Loan Type form: the Category dropdown reads
   * `LoanTypeCategoryService.getOptions()` instead, because that endpoint returns active-only
   * rows in Display Order. Kept for ad-hoc lookups of the same table.
   */
  getLoanTypeCategories(): Observable<LookupSelectOption[]> {
    return this.getOptions(LOOKUP_TABLES.LoanTypeCategories);
  }

  getFinanceFrameworks(): Observable<LookupSelectOption[]> {
    return this.getOptions(LOOKUP_TABLES.FinanceFrameworks);
  }

  getConditionTypes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ConditionTypes);
  }

  getConditionTriggerEvents(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ConditionTriggerEvents);
  }

  getConditionEvidenceTypes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ConditionEvidenceTypes);
  }

  getConditionVerificationMethods(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ConditionVerificationMethods);
  }

  getCovenantMeasurementTypes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.CovenantMeasurementTypes);
  }

  getCovenantOperators(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.CovenantOperators);
  }

  getCovenantUnitTypes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.CovenantUnitTypes);
  }

  getCovenantEvidenceTypes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.CovenantEvidenceTypes);
  }

  getCovenantVerificationMethods(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.CovenantVerificationMethods);
  }

  /**
   * Calculation Profile is persisted as the lookup ALIAS (UPPER_SNAKE, like every other covenant
   * lookup), not the Id — dbo.Covenants stores the string. Options are therefore keyed by alias,
   * unlike every other helper here.
   *
   * Falls back to the static aliases when the table is unavailable — an API that predates
   * 2026-09-07_AddCovenantCalculationProfileLookup.sql returns 404/empty, and the screen must not
   * lose a required dropdown until that script is run.
   */
  getCovenantCalculationProfiles(): Observable<AliasSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.CovenantCalculationProfiles).pipe(
      map((options) =>
        options
          .filter((option) => !!option.alias)
          .map((option) => ({ label: option.label, value: String(option.alias) })),
      ),
      map((options) => (options.length ? options : this.staticCalculationProfiles())),
      catchError(() => of(this.staticCalculationProfiles())),
    );
  }

  private staticCalculationProfiles(): AliasSelectOption[] {
    return V2_CALCULATION_PROFILE_OPTIONS.map((option) => ({ ...option }));
  }

  getClauseNegotiabilities(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ClauseNegotiabilities);
  }

  getClauseDeviationRisks(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ClauseDeviationRisks);
  }

  getClauseWordingFormats(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ClauseWordingFormats);
  }

  /**
   * Languages for T&C Catalogue Language select (`languageCode` aliases, e.g. en / ar).
   * Inactive rows are excluded — use getByTable when an existing record must still show a retired language.
   */
  getLanguages(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.Languages);
  }

  /**
   * Version status ids are IDENTITY values and differ per database, so they are read from the
   * lookup rather than assumed. Falls back to the static aliases when the table is unavailable -
   * an API that predates 2026-08-20_ClauseVersioning.sql still returns 404 here, and the screen
   * matches on the alias anyway.
   */
  getClauseVersionStatuses(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ClauseVersionStatuses).pipe(
      map((options) => (options.length ? options : this.staticClauseVersionStatuses())),
      catchError(() => of(this.staticClauseVersionStatuses())),
    );
  }

  private staticClauseVersionStatuses(): LookupSelectOption[] {
    return [
      { label: 'Draft', value: 1, alias: 'DRAFT' },
      { label: 'Approved', value: 2, alias: 'APPROVED' },
      { label: 'Retired', value: 3, alias: 'RETIRED' },
    ];
  }

  getFinanceFrameworksOnce(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.FinanceFrameworks);
  }

  getSecurityProviderTypes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.SecurityProviderTypes);
  }

  getSecurityVerificationProfiles(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.SecurityVerificationProfiles);
  }

  getCollateralValuationFrequencies(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.CollateralValuationFrequencies);
  }

  getDocumentProvisionMethods(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.DocumentProvisionMethods);
  }

  getDocumentVerificationMethods(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.DocumentVerificationMethods);
  }

  getDocumentSignatureRequirements(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.DocumentSignatureRequirements);
  }

  getDocumentOriginalRequirements(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.DocumentOriginalRequirements);
  }

  getDocumentRevisionPolicies(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.DocumentRevisionPolicies);
  }

  getRuleSetStatuses(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.RuleSetStatuses);
  }

  getApplicabilityLevels(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ApplicabilityLevels);
  }

  getComponentTypes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ComponentTypes);
  }

  getFieldDataTypes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.FieldDataTypes);
  }

  getFieldControlTypes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.FieldControlTypes);
  }

  getFieldValidationRules(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.FieldValidationRules);
  }

  getFieldValueSources(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.FieldValueSources);
  }

  getFieldCardinalityCapabilities(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.FieldCardinalityCapabilities);
  }

  getFieldValueShapes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.FieldValueShapes);
  }

  getFieldDefaultScopes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.FieldDefaultScopes);
  }

  getFieldTemporalModes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.FieldTemporalModes);
  }

  getFieldPostActivationChanges(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.FieldPostActivationChanges);
  }

  getFieldValueHandlers(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.FieldValueHandlers);
  }

  getFieldFormatTypes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.FieldFormatTypes);
  }

  getFieldValidationProfiles(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.FieldValidationProfiles);
  }

  /**
   * LookupApprovalTypes / LookupApprovalDecisions — the approval vocabulary (requirement §13).
   *
   * Read from the database rather than declared in the client. The aliases are the keys the write
   * side validates against, and hard-coding them here previously shipped two that do not exist:
   * OPERATIONS (the business wording; the stored alias is OPERATIONS_READINESS) and PENDING (not a
   * decision at all). Labels come from the lookup's own NameEn/NameAr, so they are translated
   * without needing a key per value.
   */
  getApprovalTypes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ApprovalTypes);
  }

  getApprovalDecisions(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ApprovalDecisions);
  }

  /**
   * LookupCurrencies with currency-style labels (ISO when available).
   * e.g. `SAR — Saudi Riyal`, not snake_case aliases / country-looking bare names.
   */
  getCurrencies(): Observable<LookupSelectOption[]> {
    return combineLatest([this.getByTable(LOOKUP_TABLES.Currencies), this.lang$().pipe(take(1))]).pipe(
      take(1),
      map(([items, lang]) =>
        items
          .filter((item) => item.isActive !== false)
          .map((item) => {
            const name = this.resolveLabel(item, lang);
            const iso = this.resolveCurrencyIso(item, name);
            const label = iso && name && iso !== name ? `${iso} — ${name}` : name || iso || String(item.id);
            return {
              value: item.id,
              alias: item.alias,
              code: iso || item.code,
              label,
            };
          }),
      ),
    );
  }

  /** V2 Product Creation — period / cadence / scope / ranking / perfection. */
  getConditionOccurrences(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ConditionOccurrences);
  }

  getTimingRelationships(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.TimingRelationships);
  }

  getTimeUnits(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.TimeUnits);
  }

  getConditionCadences(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ConditionCadences);
  }

  getMeasurementScopes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.MeasurementScopes);
  }

  getAssessmentAnchorTypes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.AssessmentAnchorTypes);
  }

  getThresholdValueTypes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.ThresholdValueTypes);
  }

  getRankingRequirements(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.RankingRequirements);
  }

  getPerfectionMethods(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.PerfectionMethods);
  }

  getSecuritySatisfactionRules(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.SecuritySatisfactionRules);
  }

  getCollateralAssetTypes(): Observable<LookupSelectOption[]> {
    return this.getOptionsOnce(LOOKUP_TABLES.CollateralAssetTypes);
  }

  /**
   * Alias-keyed options for enums stored/filtered by code (e.g. DRAFT, GLOBAL, FIELD).
   * Prefer this for Rule Engine until payloads switch fully to numeric lookup ids.
   */
  getAliasOptionsOnce(tableName: LookupTableName): Observable<Array<{ label: string; value: string; id: number }>> {
    return combineLatest([this.getByTable(tableName), this.lang$().pipe(take(1))]).pipe(
      take(1),
      map(([items, lang]) =>
        items
          .filter((item) => item.isActive !== false && !!String(item.alias ?? '').trim())
          .map((item) => ({
            id: item.id,
            value: String(item.alias).trim(),
            label: this.resolveLabel(item, lang),
          })),
      ),
    );
  }

  /** Drop cached lookup HTTP streams (e.g. after admin data changes). */
  clearCache(tableName?: LookupTableName): void {
    if (tableName) {
      this.tableCache.delete(String(tableName));
      return;
    }
    this.tableCache.clear();
  }

  /**
   * Map lookup rows to select options using the given (or current) language.
   * Inactive rows are excluded by default.
   */
  toSelectOptions(
    items: LookupDto[],
    lang?: string | null,
    options?: { includeInactive?: boolean },
  ): LookupSelectOption[] {
    const includeInactive = options?.includeInactive === true;
    return items
      .filter((item) => includeInactive || item.isActive !== false)
      .map((item) => ({
        value: item.id,
        alias: item.alias,
        nameEn: item.nameEn?.trim() || undefined,
        label: this.resolveLabel(item, lang),
      }));
  }

  /** Pick nameEn vs nameAr from UI language (en → English; ar/sa → Arabic). */
  resolveLabel(item: LookupDto, lang?: string | null): string {
    const code = (lang ?? this.resolveLang()).toLowerCase();
    const useArabic = code === 'ar' || code === 'sa';
    if (useArabic) {
      return item.nameAr?.trim() || item.nameEn?.trim() || item.alias || String(item.id);
    }
    return item.nameEn?.trim() || item.nameAr?.trim() || item.alias || String(item.id);
  }

  private lang$(): Observable<string> {
    return merge(
      of(this.resolveLang()),
      this.translate.onLangChange.pipe(map((event) => event.lang)),
    ).pipe(distinctUntilChanged());
  }

  private resolveLang(): string {
    return this.translate.currentLang || localStorage.getItem('lang') || 'en';
  }

  private unwrapList<T>(res: ApiResponse<T[]> | T[]): T[] {
    if (Array.isArray(res)) return res;
    if (res && Array.isArray(res.data)) return res.data;
    return [];
  }

  /**
   * Resolve ISO 4217 code from lookup row: explicit code fields, 3-letter alias,
   * or match name against Intl currency display names.
   */
  private resolveCurrencyIso(item: LookupDto, name: string): string {
    for (const raw of [item.code, item.alias]) {
      const s = String(raw ?? '').trim();
      if (/^[A-Za-z]{3}$/.test(s)) return s.toUpperCase();
    }

    const target = String(name ?? '').trim().toLowerCase();
    if (!target) return '';

    try {
      const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
      if (typeof intl.supportedValuesOf !== 'function') return '';
      const display = new Intl.DisplayNames(['en'], { type: 'currency' });
      for (const code of intl.supportedValuesOf('currency')) {
        const label = display.of(code);
        if (label && label.toLowerCase() === target) return code;
      }
    } catch {
      /* Intl currency support unavailable */
    }
    return '';
  }

  private normalize(row: unknown): LookupDto {
    const r = (row ?? {}) as Record<string, unknown>;
    const alias = String(r['alias'] ?? '').trim();
    const code = String(r['code'] ?? r['isoCode'] ?? r['currencyCode'] ?? '').trim();
    return {
      id: Number(r['id'] ?? 0),
      alias: alias || code,
      code: code || undefined,
      nameEn: String(r['nameEn'] ?? r['name'] ?? r['label'] ?? ''),
      nameAr: String(r['nameAr'] ?? r['name'] ?? r['label'] ?? ''),
      isActive: typeof r['isActive'] === 'boolean' ? r['isActive'] : true,
    };
  }
}
